/**
 * How did this person find us?
 *
 * WHY THIS EXISTS (10 Sept 2026): for ten weeks a signup could not be attributed
 * to any channel, and the true number of strangers who have ever signed up is
 * zero. **The first one must not be a mystery**, so this ships before any of the
 * page or content work rather than after it.
 *
 * FIRST TOUCH WINS. The channel that brought someone is the one that did the
 * work; if they come back later by typing the domain, that later visit must not
 * overwrite it. So the value is written once and never replaced.
 *
 * BEST EFFORT, NEVER BLOCKING. Every read and write is wrapped: localStorage
 * throws in some private-browsing modes, and a signup must never fail because
 * attribution was unavailable. A missing value is a fact about the arrival.
 */

const SOURCE_KEY = 'am_signup_source';
const REFERRER_KEY = 'am_signup_referrer';

/** Tags we set ourselves, e.g. agent-mesh.org/?from=youtube in a video description. */
const SOURCE_PARAMS = ['from', 'utm_source', 'ref'];

function readStore(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStoreOnce(key: string, value: string): void {
  try {
    if (!localStorage.getItem(key)) localStorage.setItem(key, value);
  } catch {
    /* private mode, blocked storage — proceed without attribution */
  }
}

/** Host only. A full referrer URL can carry a search query or a session id. */
const bare = (h: string) => h.replace(/^www\./, '').toLowerCase();

function referrerHost(): string | null {
  try {
    if (!document.referrer) return null;
    // Normalise BOTH sides before comparing. Getting this order wrong means a
    // visit from www.agent-mesh.org is not recognised as our own host and gets
    // recorded as a referrer of agent-mesh.org — the site counting itself as a
    // source. A test caught exactly that.
    const host = bare(new URL(document.referrer).hostname);
    if (host === bare(window.location.hostname)) return null;
    return host;
  } catch {
    return null;
  }
}

/**
 * Call once, as early as possible, on every page load. Records the first
 * external thing that sent this browser here and then leaves it alone.
 */
export function captureAttribution(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    for (const p of SOURCE_PARAMS) {
      const v = params.get(p);
      if (v) {
        writeStoreOnce(SOURCE_KEY, v.slice(0, 64));
        break;
      }
    }
    const host = referrerHost();
    if (host) writeStoreOnce(REFERRER_KEY, host.slice(0, 255));
  } catch {
    /* never let attribution break a page load */
  }
}

/** What to send with a registration. Both fields may be absent. */
export function getAttribution(): { signupSource?: string; signupReferrer?: string } {
  const source = readStore(SOURCE_KEY);
  const referrer = readStore(REFERRER_KEY);
  return {
    ...(source ? { signupSource: source } : {}),
    ...(referrer ? { signupReferrer: referrer } : {}),
  };
}
